// Éditeur de devis : révisions et variantes, lignes par section (tableau sur ordinateur, cartes sur mobile),
// ajout depuis le catalogue / les exigences non chiffrées / ligne libre, totaux, paramètres, hypothèses et exclusions,
// flux de validation et d'émission (dérogation motivée), révision émise en lecture seule, exports.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BadgeCheck, BookOpen, Boxes, CopyPlus, FileDown, FilePlus2, FileSearch, FileSpreadsheet, HardHat, ListChecks, Lock, Pencil, Plus, Send, ShieldCheck, Stamp, Undo2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { dateHeure, euros, pluriel } from '../../lib/format.ts';
import {
  Alerte, Bouton, Chargement, EtatErreur, EtatVide, IndicateurEnregistrement, LienBouton, Panneau, useConfirmation, useNotifier,
  type EtatEnregistrement,
} from '../../components/ui.tsx';
import { BadgeStatutDevis, STATUTS_DEVIS } from '../../components/statuts.tsx';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import {
  BadgeARenseigner, BadgesAlertePrix, LIBELLES_MOTIFS, LIBELLES_SECTIONS, Montant, pct, quantite, SaisieAuto, SECTIONS, sourcePrix, useEtroit, versSaisie,
  type Bloc, type Ligne, type Section, type VueRevision,
} from './commun.tsx';
import { DialogueCatalogue, DialogueDerogation, DialogueExigences, DialogueLigne, DialogueVariante } from './dialogues.tsx';
import { DialogueOuvrage } from './ouvrages.tsx';
import { BandeauAcceptation, DialogueAcceptation, DialogueAnnulationAcceptation } from './acceptation.tsx';
import { DialogueRevuePrix, nbAlertes } from './tarifs.tsx';
import { EcartsMetres } from './EcartsMetres.tsx';

interface DevisEntete { id: string; number: string; title: string; revisions: { id: string; rev_no: number; variant: string; status: string }[] }

function revisionParDefaut(revs: DevisEntete['revisions']) {
  const base = revs.filter((r) => r.variant === 'Base');
  const liste = base.length ? base : revs;
  return [...liste].sort((a, b) => b.rev_no - a.rev_no)[0]?.id;
}

export function PageEditeurDevis() {
  const { id = '', quoteId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const devis = useQuery({ queryKey: ['devis', quoteId], queryFn: () => api.get<DevisEntete>(`/api/devis/${quoteId}`) });
  const revisionId = params.get('revision') ?? (devis.data ? revisionParDefaut(devis.data.revisions) : undefined);
  const vue = useQuery({ queryKey: ['revision', revisionId], enabled: !!revisionId,
    queryFn: () => api.get<VueRevision>(`/api/devis/revisions/${revisionId}`) });

  return (
    <DossierCadre dossierId={id} actif="devis" large
      actions={<LienBouton to={`/etudes/${id}/devis`} icone={<FileSpreadsheet aria-hidden />}>Tous les devis</LienBouton>}>
      {(devis.isLoading || vue.isLoading) && <Chargement lignes={6} />}
      {devis.isError && <EtatErreur erreur={devis.error} reessayer={() => devis.refetch()} />}
      {vue.isError && <EtatErreur erreur={vue.error} reessayer={() => vue.refetch()} />}
      {devis.data && !revisionId && <EtatVide titre="Aucune révision" texte="Ce devis ne contient aucune révision." />}
      {vue.data && <Editeur v={vue.data} dossierId={id} changerRevision={(rid) => setParams({ revision: rid })} />}
    </DossierCadre>
  );
}

function Editeur({ v, dossierId, changerRevision }: { v: VueRevision; dossierId: string; changerRevision: (rid: string) => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const etroit = useEtroit(900);
  const { confirmer, element: confirmation } = useConfirmation();
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const [quand, setQuand] = useState<string | undefined>();
  const [dialogue, setDialogue] = useState<null | 'ligne' | 'catalogue' | 'ouvrage' | 'exigences' | 'derogation' | 'variante' | 'acceptation' | 'annulation' | 'revue'>(null);
  const [ligneEditee, setLigneEditee] = useState<Ligne | null>(null);
  const [flux, setFlux] = useState(false);
  const r = v.revision;
  const rid = r.id;
  const d = v.droits;
  const nav = useNavigate();
  const preparerChantier = async () => {
    setFlux(true);
    try {
      const c = await api.post<{ id: string }>(`/api/devis/revisions/${rid}/chantier`, {});
      nav(`/interventions/chantiers/${c.id}/preparer`);
    } catch (e) { notifier(messageErreur(e), true); setFlux(false); }
  };
  const modifiable = d.ecrire && r.status === 'brouillon';
  useEffect(() => { setEtat('enregistre'); setQuand(undefined); }, [rid]);

  const rafraichir = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['revision', rid] }), qc.invalidateQueries({ queryKey: ['devis', v.devis.id] }),
      qc.invalidateQueries({ queryKey: ['devis-dossier', dossierId] }), qc.invalidateQueries({ queryKey: ['exigences-non-chiffrees'] }),
      qc.invalidateQueries({ queryKey: ['dossier', dossierId] }),
    ]);
  };
  /** Toute écriture passe ici : indicateur d'enregistrement, message d'erreur, rafraîchissement des totaux calculés par le serveur. */
  const ecrire = async <T,>(f: () => Promise<T>): Promise<T> => {
    setEtat('en_cours');
    try {
      const x = await f();
      await rafraichir();
      setEtat('enregistre'); setQuand(`à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`);
      return x;
    } catch (e) {
      setEtat('erreur'); notifier(messageErreur(e), true);
      throw e;
    }
  };
  const patchLigne = (lid: string, corps: Record<string, unknown>) => ecrire(() => api.patch(`/api/devis/revisions/${rid}/lignes/${lid}`, corps));
  const patchRevision = (corps: Record<string, unknown>) => ecrire(() => api.patch(`/api/devis/revisions/${rid}`, corps));
  /** Mise à jour EXPLICITE du prix de lignes au tarif proposé (jamais silencieuse). */
  const actualiserPrix = async (lignes: { ligneId: string; priceItemId: string }[]) => {
    const x = await ecrire(() => api.post<{ misesAJour: number }>(`/api/devis/revisions/${rid}/lignes/actualiser-prix`, { lignes }));
    notifier(`${pluriel(x.misesAJour, 'prix mis à jour', 'prix mis à jour')}.`);
    await qc.invalidateQueries({ queryKey: ['prix-reference'] });
  };
  const alertes = nbAlertes(v);
  const action = async (chemin: string, corps?: unknown, message?: string) => {
    setFlux(true);
    try { await ecrire(() => api.post(`/api/devis/revisions/${rid}/${chemin}`, corps)); if (message) notifier(message); }
    catch { /* déjà signalé */ } finally { setFlux(false); }
  };
  const nouvelleRevision = async (variante?: string) => {
    const x = await ecrire(() => api.post<{ revisionId: string; revNo: number; variante: string }>(`/api/devis/${v.devis.id}/revisions`,
      { depuisRevisionId: rid, variante: variante ?? null }));
    notifier(variante ? `Variante « ${x.variante} » créée.` : `Révision ${x.revNo} créée en brouillon.`);
    changerRevision(x.revisionId);
  };
  const emettre = async () => {
    if (!v.totaux.complet) {
      if (d.derogation) setDialogue('derogation');
      else notifier(`Émission bloquée : ${pluriel(v.totaux.nb_a_renseigner, 'ligne')} à renseigner. Seul un rôle habilité peut accorder une dérogation.`, true);
      return;
    }
    if (await confirmer('Émettre cette révision ?', 'La révision sera figée : lignes, totaux, hypothèses et exclusions ne pourront plus être modifiés. Le PDF client émis sera conservé.', 'Émettre et figer')) {
      await action('emettre', {}, 'Révision émise et figée.');
    }
  };

  const lignesTriees = useMemo(() => [...v.lignes].sort((a, b) => a.position - b.position), [v.lignes]);
  const deplacer = async (l: Ligne, sens: -1 | 1) => {
    const meme = lignesTriees.filter((x) => x.section === l.section);
    const i = meme.findIndex((x) => x.id === l.id);
    const voisin = meme[i + sens];
    if (!voisin) return;
    const ids = lignesTriees.map((x) => x.id);
    const a = ids.indexOf(l.id); const b = ids.indexOf(voisin.id);
    [ids[a], ids[b]] = [ids[b], ids[a]];
    await ecrire(() => api.post(`/api/devis/revisions/${rid}/lignes/ordre`, { ids }));
  };
  const supprimer = async (l: Ligne) => {
    if (await confirmer('Supprimer la ligne ?', `« ${l.label} » sera retirée de cette révision.`, 'Supprimer la ligne')) {
      await ecrire(() => api.del(`/api/devis/revisions/${rid}/lignes/${l.id}`));
    }
  };

  const t = v.totaux;
  const resumeTotal = t.complet
    ? <span className="devis-resume-total"><span className="petit secondaire">Total TTC</span> <Montant v={t.totaux!.ttc} fort /></span>
    : <span className="devis-resume-total"><BadgeARenseigner /> <span className="petit">{`Total incomplet : ${pluriel(t.nb_a_renseigner, 'ligne')} à renseigner`}</span></span>;

  // Action principale du flux selon l'état
  let principal: ReactNode = null;
  const secondaires: ReactNode[] = [];
  if (r.status === 'brouillon' && d.ecrire) principal = <Bouton variante="principal" icone={<Send aria-hidden />} chargement={flux} onClick={() => action('soumettre', undefined, 'Révision soumise à validation.')}>Soumettre à validation</Bouton>;
  if (r.status === 'a_valider' && d.valider) principal = <Bouton variante="principal" icone={<ShieldCheck aria-hidden />} chargement={flux} onClick={() => action('valider', undefined, 'Révision validée.')}>Valider</Bouton>;
  if (r.status === 'validee' && d.emettre) principal = <Bouton variante="principal" icone={<Stamp aria-hidden />} chargement={flux} onClick={emettre}>Émettre</Bouton>;
  if (r.fige && d.ecrire) principal = <Bouton variante="principal" icone={<FilePlus2 aria-hidden />} onClick={() => nouvelleRevision()}>Nouvelle révision</Bouton>;
  // Carnet de chantier : une révision acceptée (en cours) devient un chantier ; s'il existe déjà, on l'ouvre.
  if (v.chantier) {
    secondaires.push(<LienBouton key="chantier" icone={<HardHat aria-hidden />} to={`/interventions/chantiers/${v.chantier.id}`}>
      {v.chantier.reference ? `Chantier ${v.chantier.reference}` : 'Chantier en préparation'}</LienBouton>);
  } else if (r.status === 'emise' && v.acceptation && !v.acceptation.remplacee && d.preparer_chantier) {
    if (r.fige && d.ecrire) secondaires.push(<Bouton key="nouvelle-revision" icone={<FilePlus2 aria-hidden />} onClick={() => nouvelleRevision()}>Nouvelle révision</Bouton>);
    principal = <Bouton variante="principal" icone={<HardHat aria-hidden />} chargement={flux} onClick={preparerChantier}>Préparer le chantier</Bouton>;
  }
  // Révision émise en cours, pas encore acceptée : « Marquer accepté par le client » (chargé d'affaires, administrateur).
  if (r.status === 'emise' && !v.acceptation && d.accepter) {
    secondaires.push(<Bouton key="accepter" icone={<BadgeCheck aria-hidden />} onClick={() => setDialogue('acceptation')}>Marquer accepté par le client</Bouton>);
  }
  if ((r.status === 'a_valider' || r.status === 'validee') && d.ecrire) {
    secondaires.push(<Bouton key="brouillon" icone={<Undo2 aria-hidden />} onClick={() => action('brouillon', undefined, 'Révision repassée en brouillon.')}>Repasser en brouillon</Bouton>);
  }

  return (
    <div className="pile-l devis-editeur devis-page">
      <EcartsMetres revisionId={rid} />
      {/* ── Barre du devis : révision, état, total, flux, exports ── */}
      <section className="panneau devis-barre" aria-label="Révision et actions">
        <div className="devis-barre-haut">
          <div className="pile" style={{ gap: 2, minWidth: 0 }}>
            <h2 className="devis-titre"><span className="mono">{v.devis.number}</span> {v.devis.title}</h2>
            <div className="rangee" style={{ gap: 8 }}>
              <label className="visuellement-cache" htmlFor="choix-revision">Révision affichée</label>
              <select id="choix-revision" className="saisie devis-choix-revision" value={rid} onChange={(e) => changerRevision(e.target.value)}>
                {v.revisions.map((x) => <option key={x.id} value={x.id}>{`${x.variant} — révision ${x.rev_no} (${STATUTS_DEVIS[x.status]?.libelle ?? x.status})`}</option>)}
              </select>
              <BadgeStatutDevis statut={r.status} />
              {modifiable && <IndicateurEnregistrement etat={etat} quand={quand} />}
              {/* Accès direct à la revue des prix dès le haut de l'écran (sur téléphone, le bandeau détaillé est plus bas). */}
              {(alertes.anciens > 0 || alertes.propositions > 0) && (
                <Bouton compact icone={<AlertTriangle aria-hidden />} className="devis-acces-revue" onClick={() => setDialogue('revue')}>
                  {`Prix à revoir (${Object.values(v.alertes_prix?.lignes ?? {}).filter((a) => a.ancien || a.proposition).length})`}</Bouton>
              )}
            </div>
          </div>
          {resumeTotal}
        </div>
        <div className="devis-barre-actions">
          {/* Téléphone et tablette : l'action principale passe dans la barre collante du bas (voir la fin de la page). */}
          {principal && <span className="devis-principal-haut">{principal}</span>}
          {secondaires}
          <a className="btn" href={`/api/devis/revisions/${rid}/client.pdf`} target="_blank" rel="noopener"><FileDown aria-hidden />PDF client</a>
          {d.couts && <a className="btn" href={`/api/devis/revisions/${rid}/interne.xlsx`}><FileSpreadsheet aria-hidden />Tableau interne</a>}
          {d.ecrire && <Bouton icone={<CopyPlus aria-hidden />} onClick={() => setDialogue('variante')}>Créer une variante</Bouton>}
        </div>
      </section>

      {r.fige && (
        <Alerte ton="info" titre={r.emitted_at ? `Version émise le ${dateHeure(r.emitted_at)} — figée` : 'Version figée'}>
          {r.status === 'remplacee' ? 'Cette révision a été remplacée par une révision plus récente. ' : ''}
          {r.emitted_by_name ? `Émise par ${r.emitted_by_name}. ` : ''}Lignes, totaux, hypothèses et exclusions sont conservés tels qu’émis.
          {d.ecrire && ' Pour modifier le devis, créez une nouvelle révision.'}
        </Alerte>
      )}
      {v.acceptation && <BandeauAcceptation a={v.acceptation} peutAnnuler={!!d.accepter} annuler={() => setDialogue('annulation')} />}
      {v.derogation && (
        <Alerte ton="ambre" titre="Émis avec dérogation">
          <span>« {v.derogation.motif} » — accordée par {v.derogation.auteur} le {dateHeure(v.derogation.date)}.</span>
          <span> Lignes concernées : {v.derogation.lignes.map((l) => l.label).join(', ')}.</span>
        </Alerte>
      )}
      {(r.status === 'a_valider' || r.status === 'validee') && (
        <Alerte ton="info" titre={r.status === 'a_valider' ? 'En attente de validation' : `Validée${r.validated_by_name ? ` par ${r.validated_by_name}` : ''}${r.validated_at ? ` le ${dateHeure(r.validated_at)}` : ''}`}>
          La révision n’est plus modifiable. {d.ecrire ? 'Repassez-la en brouillon pour la corriger.' : ''}
        </Alerte>
      )}
      {(alertes.anciens > 0 || alertes.propositions > 0) && v.alertes_prix && (
        <Alerte ton="ambre" titre="Prix à revoir"
          action={<Bouton compact onClick={() => setDialogue('revue')}>Revoir les prix</Bouton>}>
          {[alertes.anciens ? `${pluriel(alertes.anciens, 'prix d’achat', 'prix d’achat')} de plus de ${v.alertes_prix.seuil_mois} mois` : null,
            alertes.propositions ? `${pluriel(alertes.propositions, 'tarif plus récent disponible', 'tarifs plus récents disponibles')}` : null].filter(Boolean).join(' ; ')}.
          {' '}Rien n’est mis à jour sans votre accord.
        </Alerte>
      )}
      {!t.complet && (
        <Alerte ton="ambre" titre={`Total incomplet : ${pluriel(t.nb_a_renseigner, 'ligne')} à renseigner`}>
          <ul className="devis-liste-simple">
            {t.lignes_a_renseigner.map((l) => <li key={l.id}><strong>{l.label}</strong> — {l.motifs.map((m) => LIBELLES_MOTIFS[m] ?? m).join(' ; ')}</li>)}
          </ul>
          {r.status === 'validee' && !r.fige && <span>L’émission sera bloquée tant que ces lignes ne sont pas renseignées, sauf dérogation motivée.</span>}
        </Alerte>
      )}

      {/* ── Postes ── */}
      <Panneau titre={`Postes (${v.lignes.length})`} serre actions={modifiable && (
        <div className="groupe-btn">
          {/* Proposition depuis le CCTP, le DPGF et les métrés : écran de revue dédié (rien n'est ajouté sans validation). */}
          <LienBouton compact icone={<FileSearch aria-hidden />} to={`/etudes/${dossierId}/devis/${v.devis.id}/proposition?revision=${rid}`}>Chiffrage proposé (CCTP, DPGF)</LienBouton>
          {d.couts && <Bouton compact icone={<Boxes aria-hidden />} onClick={() => setDialogue('ouvrage')}>Ouvrage type</Bouton>}
          {d.couts && <Bouton compact icone={<BookOpen aria-hidden />} onClick={() => setDialogue('catalogue')}>Depuis le catalogue</Bouton>}
          <Bouton compact icone={<ListChecks aria-hidden />} onClick={() => setDialogue('exigences')}>Depuis les exigences</Bouton>
          <Bouton compact icone={<Plus aria-hidden />} onClick={() => { setLigneEditee(null); setDialogue('ligne'); }}>Ligne libre</Bouton>
        </div>
      )}>
        {!v.lignes.length && (
          <EtatVide titre="Aucune ligne" texte={modifiable
            ? 'Ajoutez les postes depuis le chiffrage proposé (CCTP, DPGF, métrés), un ouvrage type, le catalogue de prix, les exigences non chiffrées du dossier, ou en ligne libre.'
            : 'Cette révision ne contient aucune ligne.'} />
        )}
        {!!v.lignes.length && (etroit
          ? <CartesLignes v={v} lignes={lignesTriees} modifiable={modifiable} editer={(l) => { setLigneEditee(l); setDialogue('ligne'); }} />
          : <TableauLignes v={v} lignes={lignesTriees} modifiable={modifiable} patch={patchLigne}
              editer={(l) => { setLigneEditee(l); setDialogue('ligne'); }} />)}
      </Panneau>

      {/* ── Totaux et paramètres ── */}
      <div className="devis-bas">
        <Panneau titre="Totaux"><PanneauTotaux v={v} /></Panneau>
        <Panneau titre="Paramètres de calcul"><PanneauParametres v={v} modifiable={modifiable} patch={patchRevision} /></Panneau>
      </div>
      <Panneau titre="Hypothèses et exclusions">
        <div className="grille-2">
          <TexteAuto libelle="Hypothèses" valeur={r.hypotheses ?? ''} modifiable={modifiable} enregistrer={(x) => patchRevision({ hypotheses: x })}
            vide="Aucune hypothèse." aide="Conditions retenues pour chiffrer (accès, horaires, existant réputé conforme…). Figurent sur le PDF client." />
          <TexteAuto libelle="Exclusions" valeur={r.exclusions ?? ''} modifiable={modifiable} enregistrer={(x) => patchRevision({ exclusions: x })}
            vide="Aucune exclusion." aide="Prestations explicitement hors de l’offre. Figurent sur le PDF client." />
        </div>
      </Panneau>

      {/* ── Barre collante (≤ 900 px) : total et action principale toujours visibles, au-dessus des 5 onglets du bas ── */}
      {principal && (
        <div className="barre-actions barre-collante devis-barre-collante" role="region" aria-label="Action principale du devis">
          <span className="petit chiffre barre-collante-resume">{t.complet
            ? <><span className="secondaire">Total TTC</span> <Montant v={t.totaux!.ttc} fort /></>
            : <span className="devis-motif">{`${pluriel(t.nb_a_renseigner, 'ligne')} à renseigner`}</span>}</span>
          {principal}
        </div>
      )}

      {/* ── Dialogues ── */}
      <DialogueLigne ouvert={dialogue === 'ligne'} fermer={() => setDialogue(null)} vue={v} ligne={ligneEditee} actualiser={actualiserPrix}
        enregistrer={(corps) => ligneEditee ? patchLigne(ligneEditee.id, corps) : ecrire(() => api.post(`/api/devis/revisions/${rid}/lignes`, corps))}
        supprimer={ligneEditee ? () => supprimer(ligneEditee) : undefined}
        deplacer={ligneEditee ? (sens) => deplacer(ligneEditee, sens) : undefined} />
      <DialogueCatalogue ouvert={dialogue === 'catalogue'} fermer={() => setDialogue(null)}
        ajouter={(corps) => ecrire(() => api.post(`/api/devis/revisions/${rid}/lignes/depuis-catalogue`, corps))} />
      <DialogueOuvrage ouvert={dialogue === 'ouvrage'} fermer={() => setDialogue(null)} vue={v}
        inserer={async (corps) => {
          const x = await ecrire(() => api.post<{ creees: number; heures: string | null }>(`/api/devis/revisions/${rid}/lignes/depuis-ouvrage`, corps));
          notifier(`${pluriel(x.creees, 'ligne ajoutée', 'lignes ajoutées')}${x.heures !== null ? ` — ${quantite(x.heures)} h de base` : ' — temps de pose à renseigner'}.`);
        }} />
      <DialogueExigences ouvert={dialogue === 'exigences'} fermer={() => setDialogue(null)} dossierId={dossierId} revisionId={rid}
        ajouter={async (ids) => {
          const x = await ecrire(() => api.post<{ creees: number; ignorees: number }>(`/api/devis/revisions/${rid}/lignes/depuis-exigences`, { requirementIds: ids }));
          notifier(`${pluriel(x.creees, 'ligne ajoutée', 'lignes ajoutées')}${x.ignorees ? `, ${x.ignorees} déjà présente(s)` : ''}.`);
          return x;
        }} />
      <DialogueDerogation ouvert={dialogue === 'derogation'} fermer={() => setDialogue(null)} lignes={t.lignes_a_renseigner}
        emettre={async (motif) => { await ecrire(() => api.post(`/api/devis/revisions/${rid}/emettre`, { derogation: { motif } })); notifier('Révision émise avec dérogation et figée.'); }} />
      <DialogueRevuePrix ouvert={dialogue === 'revue'} fermer={() => setDialogue(null)} v={v} modifiable={modifiable} actualiser={actualiserPrix} />
      <DialogueVariante ouvert={dialogue === 'variante'} fermer={() => setDialogue(null)} creer={(nom) => nouvelleRevision(nom)} />
      <DialogueAcceptation ouvert={dialogue === 'acceptation'} fermer={() => setDialogue(null)} dossierId={dossierId} emiseLe={r.emitted_at}
        accepter={async (corps) => { await ecrire(() => api.post(`/api/devis/revisions/${rid}/acceptation`, corps)); notifier('Acceptation du client enregistrée.'); }} />
      <DialogueAnnulationAcceptation ouvert={dialogue === 'annulation'} fermer={() => setDialogue(null)}
        annuler={async (motif) => { await ecrire(() => api.post(`/api/devis/revisions/${rid}/acceptation/annuler`, { motif })); notifier('Acceptation annulée.'); }} />
      {confirmation}
    </div>
  );
}

// ─── Tableau éditable (ordinateur) ───
function TableauLignes({ v, lignes, modifiable, patch, editer }: { v: VueRevision; lignes: Ligne[]; modifiable: boolean;
  patch: (lid: string, corps: Record<string, unknown>) => Promise<unknown>; editer: (l: Ligne) => void }) {
  const couts = v.droits.couts;
  const nbCol = couts ? 11 : 8;
  const taux = v.taux_horaires;
  return (
    <div className="tableau-conteneur">
      <table className={`tableau devis-tableau ${couts ? 'devis-tableau-couts' : ''}`}>
        <thead>
          <tr>
            <th scope="col" className="devis-col-designation">Désignation</th>
            <th scope="col" className="devis-col-ref">Référence</th>
            <th scope="col" className="num devis-col-q">Quantité</th>
            <th scope="col" className="devis-col-u">Unité</th>
            {couts && <th scope="col" className="num devis-col-cout">Coût unitaire</th>}
            <th scope="col" className="num devis-col-h">Heures</th>
            {couts && <th scope="col" className="devis-col-taux">Taux horaire</th>}
            {couts && <th scope="col" className="num devis-col-pct">{v.revision.pricing_mode === 'marge' ? 'Marge %' : 'Majoration %'}</th>}
            <th scope="col" className="num">Prix unitaire HT</th>
            <th scope="col" className="num">Total HT</th>
            <th scope="col"><span className="visuellement-cache">Actions</span></th>
          </tr>
        </thead>
        {SECTIONS.map((s) => {
          const ls = lignes.filter((l) => l.section === s);
          if (!ls.length) return null;
          return (
            <tbody key={s}>
              <tr className="devis-section"><th scope="colgroup" colSpan={nbCol - 2}>{LIBELLES_SECTIONS[s as Section]}</th>
                <td className="num" colSpan={2}><SousTotal lignes={ls} /></td></tr>
              {ls.map((l) => (
                <tr key={l.id} id={`ligne-${l.id}`} className={l.calcul.complet ? undefined : 'devis-ligne-incomplete'}>
                  <td>{modifiable
                    ? <SaisieAuto aria-label="Désignation" title={l.label} valeur={l.label} enregistrer={(x) => patch(l.id, { label: x })} />
                    : <span>{l.label}</span>}
                    {l.requirement_label && <span className="tres-petit discret devis-sous">Exigence : {l.requirement_label}</span>}
                    {l.quantity_source && <span className="tres-petit discret devis-source-q">{l.quantity_source}</span>}
                  </td>
                  <td>{modifiable
                    ? <SaisieAuto aria-label="Référence" className="saisie-mono" valeur={l.reference ?? ''} enregistrer={(x) => patch(l.id, { reference: x || null })} />
                    : <span className="mono">{l.reference ?? ''}</span>}</td>
                  <td className="num">{modifiable
                    ? <SaisieAuto aria-label="Quantité" montantFr className="devis-saisie-num" valeur={versSaisie(l.quantity)} enregistrer={(x) => patch(l.id, { quantity: x })} />
                    : quantite(l.quantity)}</td>
                  <td>{modifiable
                    ? <SaisieAuto aria-label="Unité" className="devis-saisie-u" valeur={l.unit} enregistrer={(x) => patch(l.id, { unit: x || 'u' })} />
                    : l.unit}</td>
                  {couts && (
                    <td className="num">
                      {modifiable
                        ? <SaisieAuto aria-label="Coût unitaire" montantFr placeholder="À renseigner" className="devis-saisie-num" valeur={versSaisie(l.unit_cost)}
                            enregistrer={(x) => patch(l.id, { unitCost: x.trim() === '' ? null : x })} />
                        : <Montant v={l.unit_cost} />}
                      {l.unit_cost === null || l.unit_cost === undefined
                        ? modifiable && <span className="devis-sous"><BadgeARenseigner /></span>
                        : <span className="tres-petit discret devis-sous">{sourcePrix(l)}</span>}
                      {v.alertes_prix && <BadgesAlertePrix a={v.alertes_prix.lignes[l.id]} seuil={v.alertes_prix.seuil_mois} />}
                    </td>
                  )}
                  <td className="num">{modifiable
                    ? <SaisieAuto aria-label="Heures unitaires" montantFr className="devis-saisie-num" valeur={versSaisie(l.labor_hours)}
                        placeholder={l.labor_hours === null ? 'À renseigner' : undefined}
                        enregistrer={(x) => patch(l.id, { laborHours: x.trim() === '' ? '0' : x })} />
                    : l.labor_hours === null ? <span className="tres-petit devis-motif">À renseigner</span> : quantite(l.labor_hours)}</td>
                  {couts && (
                    <td>{modifiable
                      ? <select className="saisie" aria-label="Taux horaire" value={l.labor_rate_code ?? ''}
                          onChange={(e) => { void patch(l.id, { laborRateCode: e.target.value || null }).catch(() => {}); }}>
                          <option value="">Aucun</option>
                          {taux.filter((x) => x.active || x.code === l.labor_rate_code).map((x) => <option key={x.code} value={x.code}>{x.code}</option>)}
                        </select>
                      : <span className="mono">{l.labor_rate_code ?? ''}</span>}
                      {l.calcul.cout_horaire && <span className="tres-petit discret devis-sous">{euros(l.calcul.cout_horaire)} / h</span>}
                    </td>
                  )}
                  {couts && (
                    <td className="num">{modifiable
                      ? <SaisieAuto aria-label="Taux propre à la ligne" montantFr className="devis-saisie-num" placeholder={versSaisie(v.revision.default_rate)}
                          valeur={versSaisie(l.rate_override)} enregistrer={(x) => patch(l.id, { rateOverride: x.trim() === '' ? null : x })} />
                      : pct(l.calcul.taux_applique)}
                      {l.calcul.marge_pct && <span className="tres-petit discret devis-sous">marge {pct(l.calcul.marge_pct)}</span>}
                    </td>
                  )}
                  <td className="num">{l.calcul.prix_unitaire_ht === null ? <span className="tres-petit devis-motif">À renseigner</span> : <Montant v={l.calcul.prix_unitaire_ht} />}
                    {l.calcul.prix_convenu && <span className="tres-petit discret devis-sous" title="Prix du document signé ou commandé : il ne dépend pas du coût">Prix convenu</span>}</td>
                  <td className="num"><Montant v={l.calcul.total_ht} fort />
                    {!l.calcul.complet && <span className="tres-petit devis-motif devis-sous">{l.calcul.a_renseigner.map((m) => LIBELLES_MOTIFS[m] ?? m).join(' ; ')}</span>}
                  </td>
                  <td>{modifiable && <Bouton compact variante="fantome" className="btn-icone" aria-label={`Modifier « ${l.label} »`} title="Modifier, déplacer, supprimer"
                    onClick={() => editer(l)}><Pencil aria-hidden /></Bouton>}</td>
                </tr>
              ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

function SousTotal({ lignes }: { lignes: Ligne[] }) {
  if (lignes.some((l) => l.calcul.total_ht === null)) return <span className="tres-petit discret">Sous-total incomplet</span>;
  const centimes = lignes.reduce((s, l) => s + BigInt(l.calcul.total_ht!.replace('.', '')), 0n); // somme exacte en centimes
  const txt = `${centimes / 100n}.${String(centimes % 100n).padStart(2, '0')}`;
  return <span className="chiffre petit secondaire">{euros(txt)}</span>;
}

// ─── Cartes (mobile) ───
function CartesLignes({ v, lignes, modifiable, editer }: { v: VueRevision; lignes: Ligne[]; modifiable: boolean; editer: (l: Ligne) => void }) {
  const couts = v.droits.couts;
  return (
    <div className="devis-cartes">
      {SECTIONS.map((s) => {
        const ls = lignes.filter((l) => l.section === s);
        if (!ls.length) return null;
        return (
          <section key={s} className="devis-cartes-section" aria-label={LIBELLES_SECTIONS[s]}>
            <h3 className="devis-cartes-titre"><span>{LIBELLES_SECTIONS[s]}</span><SousTotal lignes={ls} /></h3>
            {ls.map((l) => (
              <article key={l.id} className={`devis-carte ${l.calcul.complet ? '' : 'devis-ligne-incomplete'}`}>
                <div className="devis-carte-haut">
                  <strong className="devis-carte-titre">{l.label}</strong>
                  <Montant v={l.calcul.total_ht} fort />
                </div>
                {(l.reference || l.manufacturer) && <span className="petit"><span className="mono">{l.reference}</span>{l.manufacturer ? ` — ${l.manufacturer}` : ''}</span>}
                <span className="petit secondaire chiffre">
                  {quantite(l.quantity)} {l.unit} × {l.calcul.prix_unitaire_ht === null ? 'prix à renseigner' : euros(l.calcul.prix_unitaire_ht)}
                  {l.labor_hours === null ? ' — temps de pose à renseigner' : Number(l.labor_hours) > 0 && ` — ${quantite(l.labor_hours)} h par unité`}
                </span>
                {couts && (
                  <span className="tres-petit discret">
                    {l.unit_cost === null || l.unit_cost === undefined ? 'Coût unitaire à renseigner' : `Coût ${euros(l.unit_cost)}${sourcePrix(l) ? ` — ${sourcePrix(l)}` : ''}`}
                    {l.labor_rate_code ? ` — taux ${l.labor_rate_code}` : ''}{l.calcul.marge_pct ? ` — marge ${pct(l.calcul.marge_pct)}` : ''}
                  </span>
                )}
                {!l.calcul.complet && <span className="tres-petit devis-motif">{l.calcul.a_renseigner.map((m) => LIBELLES_MOTIFS[m] ?? m).join(' ; ')}</span>}
                {l.calcul.prix_convenu && <span className="tres-petit discret">Prix convenu (document signé ou commandé){couts && l.calcul.cout_a_renseigner?.length ? ' — coût à renseigner, marge inconnue' : ''}</span>}
                {v.alertes_prix && <BadgesAlertePrix a={v.alertes_prix.lignes[l.id]} seuil={v.alertes_prix.seuil_mois} />}
                {l.requirement_label && <span className="tres-petit discret">Exigence : {l.requirement_label}</span>}
                {l.quantity_source && <span className="tres-petit discret devis-source-q">{l.quantity_source}</span>}
                {modifiable && <Bouton compact icone={<Pencil aria-hidden />} onClick={() => editer(l)}>Modifier</Bouton>}
              </article>
            ))}
          </section>
        );
      })}
    </div>
  );
}

// ─── Totaux ───
/** Coût ou marge inconnu : une ligne à prix convenu n'a pas encore de coût (jamais assimilé à zéro). */
const INCONNU_CONVENU = 'Inconnu : coût à renseigner sur une ligne à prix convenu';
function LigneTotal({ libelle, valeur, fort }: { libelle: ReactNode; valeur: ReactNode; fort?: boolean }) {
  return <div className={`devis-total ${fort ? 'devis-total-fort' : ''}`}><dt>{libelle}</dt><dd>{valeur}</dd></div>;
}

function BlocTotaux({ b, remisePct, tva, couts, suffixe }: { b: Bloc; remisePct: string; tva: string; couts: boolean; suffixe: string }) {
  return (
    <dl className="devis-totaux">
      {couts && b.cout_revient !== undefined && <LigneTotal libelle={`Coût de revient${suffixe}`} valeur={b.cout_revient === null ? <span className="tres-petit devis-motif">{INCONNU_CONVENU}</span> : <Montant v={b.cout_revient} />} />}
      {Number(b.remise) > 0 && <LigneTotal libelle={`Total HT avant remise${suffixe}`} valeur={<Montant v={b.ht_brut} />} />}
      {Number(b.remise) > 0 && <LigneTotal libelle={`Remise ${pct(remisePct)}`} valeur={<span className="chiffre">−{euros(b.remise)}</span>} />}
      <LigneTotal libelle={`Total HT${suffixe}`} valeur={<Montant v={b.ht_net} fort />} fort />
      <LigneTotal libelle={`TVA ${pct(tva)}`} valeur={<Montant v={b.tva} />} />
      <LigneTotal libelle={`Total TTC${suffixe}`} valeur={<Montant v={b.ttc} fort />} fort />
      {couts && b.marge !== undefined && (
        <>
          <LigneTotal libelle={`Marge dégagée${suffixe}`} valeur={b.marge === null ? <span className="tres-petit devis-motif">{INCONNU_CONVENU}</span> : <Montant v={b.marge} />} />
          <LigneTotal libelle="Marge réalisée, en % du prix de vente HT" valeur={<span className="chiffre">{pct(b.marge_pct)}</span>} />
          <LigneTotal libelle="Majoration équivalente, en % du coût de revient" valeur={<span className="chiffre">{pct(b.majoration_pct)}</span>} />
        </>
      )}
    </dl>
  );
}

function PanneauTotaux({ v }: { v: VueRevision }) {
  const t = v.totaux;
  if (t.complet) return <BlocTotaux b={t.totaux!} remisePct={t.remise_pct} tva={t.taux_tva} couts={v.droits.couts} suffixe="" />;
  return (
    <div className="pile">
      <Alerte ton="ambre" titre={`Total incomplet : ${pluriel(t.nb_a_renseigner, 'ligne')} à renseigner`}>
        Les totaux du devis ne sont pas calculables. Montants ci-dessous : total partiel, hors lignes à renseigner.
      </Alerte>
      <BlocTotaux b={t.partiel!} remisePct={t.remise_pct} tva={t.taux_tva} couts={v.droits.couts} suffixe=" (hors lignes à renseigner)" />
    </div>
  );
}

// ─── Paramètres de calcul ───
function PanneauParametres({ v, modifiable, patch }: { v: VueRevision; modifiable: boolean; patch: (c: Record<string, unknown>) => Promise<unknown> }) {
  const r = v.revision;
  const couts = v.droits.couts;
  const champ = (libelle: string, valeur: string, cle: string, aide?: string, suffixe = '%') => (
    <div className="champ">
      <span className="champ-libelle" id={`lib-${cle}`}>{libelle}</span>
      {modifiable
        ? <SaisieAuto aria-labelledby={`lib-${cle}`} montantFr valeur={valeur} enregistrer={(x) => patch({ [cle]: cle === 'validityDays' ? Number(x) : x || '0' })} />
        : <span className="chiffre">{valeur} {suffixe}</span>}
      {aide && <span className="champ-aide">{aide}</span>}
    </div>
  );
  return (
    <div className="pile">
      {couts && (
        <div className="champ">
          <span className="champ-libelle" id="lib-mode">Mode de calcul du prix de vente</span>
          <div className="segment devis-segment" role="group" aria-labelledby="lib-mode">
            <button type="button" aria-pressed={r.pricing_mode === 'marge'} disabled={!modifiable}
              onClick={() => { void patch({ pricingMode: 'marge' }).catch(() => {}); }}>Marge sur prix de vente</button>
            <button type="button" aria-pressed={r.pricing_mode === 'majoration'} disabled={!modifiable}
              onClick={() => { void patch({ pricingMode: 'majoration' }).catch(() => {}); }}>Majoration du coût</button>
          </div>
          <span className="champ-aide">{r.pricing_mode === 'marge'
            ? 'Prix = coût ÷ (1 − taux) : le taux est la part de marge dans le prix de vente (25 % de marge ⇔ 33,33 % de majoration).'
            : 'Prix = coût × (1 + taux) : le taux s’applique au coût de revient (25 % de majoration ⇔ 20 % de marge).'}</span>
        </div>
      )}
      <div className="grille-2">
        {couts && champ(r.pricing_mode === 'marge' ? 'Taux de marge par défaut (%)' : 'Taux de majoration par défaut (%)', versSaisie(r.default_rate), 'defaultRate',
          'Appliqué aux lignes sans taux propre')}
        {champ('Remise globale (%)', versSaisie(r.discount_pct), 'discountPct')}
        {champ('TVA (%)', versSaisie(r.vat_rate), 'vatRate', 'Taux normal 20 % ; taux réduits 10 % ou 5,5 % selon les conditions applicables')}
        {champ('Validité de l’offre (jours)', String(r.validity_days), 'validityDays', undefined, 'jours')}
      </div>
      {!couts && <p className="petit discret"><Lock size={14} aria-hidden style={{ display: 'inline', verticalAlign: '-2px' }} /> Coûts, taux et marges réservés aux rôles habilités.</p>}
    </div>
  );
}

function TexteAuto({ libelle, valeur, modifiable, enregistrer, vide, aide }: { libelle: string; valeur: string; modifiable: boolean;
  enregistrer: (v: string) => Promise<unknown>; vide: string; aide: string }) {
  const [local, setLocal] = useState(valeur);
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setLocal(valeur); }, [valeur, focus]);
  if (!modifiable) {
    return <div className="champ"><span className="champ-libelle">{libelle}</span>
      {valeur ? <p className="devis-texte">{valeur}</p> : <span className="inconnu">{vide}</span>}</div>;
  }
  return (
    <div className="champ">
      <label className="champ-libelle" htmlFor={`txt-${libelle}`}>{libelle}</label>
      <textarea id={`txt-${libelle}`} className="saisie" rows={5} maxLength={8000} value={local}
        onFocus={() => setFocus(true)} onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { setFocus(false); if (local !== valeur) void enregistrer(local).catch(() => {}); }} />
      <span className="champ-aide">{aide}</span>
    </div>
  );
}
